<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';

const props = defineProps({
  listing: {
    type: Object,
    required: true,
  },
  shareEnabled: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['edit', 'delete', 'share']);

const { t } = useI18n();

const coverPhoto = computed(
  () =>
    props.listing.photos?.find(photo => photo.primary) ||
    props.listing.photos?.[0]
);

// Prefer the AI image (what buyers see) and always a resized variant: the index
// renders a full page of covers.
const coverPhotoUrl = computed(
  () => coverPhoto.value?.generated_card_url || coverPhoto.value?.card_url
);

const subtitle = computed(() =>
  [
    props.listing.make?.name,
    props.listing.model?.name,
    props.listing.trim?.name,
  ]
    .filter(Boolean)
    .join(' ')
);

const specChips = computed(() => {
  const chips = [];
  if (props.listing.year) chips.push(String(props.listing.year));
  if (props.listing.mileage != null) {
    chips.push(
      `${Number(props.listing.mileage).toLocaleString()} ${props.listing.mileage_unit || 'km'}`
    );
  }
  if (props.listing.transmission) {
    chips.push(
      t(`WGPT_CAR_LISTINGS.TRANSMISSIONS.${props.listing.transmission}`)
    );
  }
  if (props.listing.fuel_type) {
    chips.push(t(`WGPT_CAR_LISTINGS.FUEL_TYPES.${props.listing.fuel_type}`));
  }
  if (props.listing.condition) {
    chips.push(t(`WGPT_CAR_LISTINGS.CONDITIONS.${props.listing.condition}`));
  }
  return chips;
});

const formattedPrice = computed(() => {
  if (!props.listing.list_price) return t('WGPT_CAR_LISTINGS.CARD.NO_PRICE');
  const currency = (props.listing.currency || '').toUpperCase();
  return `${Number(props.listing.list_price).toLocaleString()} ${currency}`.trim();
});

const formattedFinalPrice = computed(() => {
  if (!props.listing.final_price) return '';
  const currency = (props.listing.currency || '').toUpperCase();
  return `${Number(props.listing.final_price).toLocaleString()} ${currency}`.trim();
});

// Only a live listing can be shared on social media.
const canShare = computed(() => props.listing.status === 'active');

const statusClass = computed(() => {
  switch (props.listing.status) {
    case 'active':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'sold':
      return 'bg-n-blue-3 text-n-blue-11';
    case 'archived':
      return 'bg-n-slate-3 text-n-slate-11';
    default:
      return 'bg-n-amber-3 text-n-amber-11';
  }
});
</script>

<template>
  <div
    class="flex flex-col overflow-hidden transition-shadow border cursor-pointer rounded-xl border-n-weak bg-n-surface-2 hover:shadow-md"
    @click="$emit('edit')"
  >
    <div
      class="relative flex items-center justify-center aspect-video bg-n-alpha-1"
    >
      <img
        v-if="coverPhoto"
        :src="coverPhotoUrl"
        :alt="listing.title"
        class="object-cover w-full h-full"
        loading="lazy"
        decoding="async"
      />
      <span v-else class="text-4xl i-lucide-car text-n-slate-8" />
      <span
        class="absolute px-2 py-0.5 text-xs font-medium rounded-full top-2 start-2"
        :class="statusClass"
      >
        {{ t(`WGPT_CAR_LISTINGS.STATUS.${listing.status}`) }}
      </span>
      <span
        v-if="listing.video"
        class="absolute flex items-center justify-center w-6 h-6 text-white rounded-full top-2 end-2 bg-[#000000]/60"
        :title="t('WGPT_CAR_LISTINGS.CARD.HAS_VIDEO')"
      >
        <span class="block text-xs i-lucide-play" />
      </span>
      <span
        v-if="listing.quantity > 1"
        class="absolute flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-white rounded-full bottom-2 end-2 bg-[#000000]/60"
        :title="
          t('WGPT_CAR_LISTINGS.CARD.QUANTITY', { count: listing.quantity })
        "
      >
        <span class="text-[0.7rem] i-lucide-layers" />
        {{ listing.quantity }}
      </span>
    </div>
    <div class="flex flex-col gap-2 p-3">
      <div class="flex items-start justify-between gap-2">
        <div class="flex flex-col min-w-0">
          <span class="font-medium truncate text-n-slate-12">
            {{ listing.title }}
          </span>
          <span class="text-xs truncate text-n-slate-10">
            {{ subtitle }} · {{ listing.reference }}
          </span>
        </div>
        <div class="flex items-center gap-1" @click.stop>
          <span
            v-if="shareEnabled"
            v-tooltip.top="
              canShare
                ? t('WGPT_CAR_LISTINGS.CARD.SHARE')
                : t('WGPT_CAR_LISTINGS.SOCIAL.UNPUBLISHED_HINT')
            "
          >
            <Button
              icon="i-lucide-share-2"
              variant="ghost"
              color="slate"
              size="xs"
              :disabled="!canShare"
              @click="$emit('share')"
            />
          </span>
          <ConfirmButton
            :confirm-label="t('WGPT_CAR_LISTINGS.DELETE.CONFIRM_YES')"
            icon="i-lucide-trash-2"
            variant="ghost"
            color="slate"
            size="xs"
            @click="$emit('delete')"
          />
        </div>
      </div>
      <div v-if="specChips.length" class="flex flex-wrap gap-1">
        <span
          v-for="chip in specChips"
          :key="chip"
          class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-1 text-n-slate-11"
        >
          {{ chip }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-base font-semibold text-n-slate-12">
          {{ formattedPrice }}
        </span>
        <span
          v-if="listing.final_price"
          class="px-1.5 py-0.5 text-xs rounded bg-n-blue-3 text-n-blue-11"
        >
          {{ t('WGPT_CAR_LISTINGS.CARD.FINAL_PRICE') }}:
          {{ formattedFinalPrice }}
        </span>
      </div>
      <div
        v-if="listing.location"
        class="flex items-center gap-1 text-xs text-n-slate-10"
      >
        <span class="i-lucide-map-pin shrink-0" />
        <span class="truncate">{{ listing.location }}</span>
      </div>
    </div>
  </div>
</template>
